import type { Paginated, SupportedLocale, VehicleClass } from '@fleetcore/shared'

import { api } from '../../lib/api-client'
import { API_PREFIX } from '../../lib/endpoints'
import { toCapabilityProfile } from './capability-adapter'
import type {
  CapabilityProfile,
  LatestTelemetryView,
  LiveVehicleEnvelope,
  VehicleStatus,
  VehicleView,
} from './api-types'

/**
 * The vehicle module's own paths.
 *
 * They live here rather than in `lib/endpoints.ts` because that file is the shared
 * authentication surface and is being edited by two other agents this phase; a
 * feature that owns four routes and no one else's does not need to contend for it.
 * `API_PREFIX` is still imported so there remains exactly one place that decides
 * the API's global prefix.
 */
export const vehicleEndpoints = {
  list: `${API_PREFIX}/vehicles`,
  detail: (id: string) => `${API_PREFIX}/vehicles/${id}`,
  /** Redis projection. Invariant 1: the live surface never scans `positions`. */
  live: (id: string) => `${API_PREFIX}/live/vehicles/${id}`,
  latestTelemetry: (id: string) => `${API_PREFIX}/vehicles/${id}/telemetry/latest`,
  capabilities: (id: string) => `${API_PREFIX}/vehicles/${id}/capabilities`,
} as const

function withLocale(path: string, locale: SupportedLocale): string {
  return `${path}?locale=${locale}`
}

export function fetchVehicle(id: string): Promise<VehicleView> {
  return api.get<VehicleView>(vehicleEndpoints.detail(id))
}

export function fetchVehicleLive(id: string): Promise<LiveVehicleEnvelope> {
  return api.get<LiveVehicleEnvelope>(vehicleEndpoints.live(id))
}

/**
 * The locale travels on the query string even though the token carries one.
 *
 * Channel labels are resolved server-side from the shared registry — 142 of them,
 * which the catalog deliberately does not duplicate — so the panel would otherwise
 * keep rendering French labels for a full page lifetime after the user switches to
 * English, with every other string on the screen having already changed.
 */
export function fetchLatestTelemetry(
  id: string,
  locale: SupportedLocale,
): Promise<LatestTelemetryView> {
  return api.get<LatestTelemetryView>(withLocale(vehicleEndpoints.latestTelemetry(id), locale))
}

export async function fetchCapabilities(
  id: string,
  locale: SupportedLocale,
): Promise<CapabilityProfile> {
  const body = await api.get<unknown>(withLocale(vehicleEndpoints.capabilities(id), locale))
  return toCapabilityProfile(body)
}

/** The carte grise fields. Each may be cleared with `null`. */
export interface CarteGriseInput {
  carteGriseNumber?: string | null
  carteGriseOwner?: string | null
  firstRegisteredOn?: string | null
  carteGriseIssuedOn?: string | null
}

export function updateVehicle(id: string, input: CarteGriseInput): Promise<VehicleView> {
  return api.patch<VehicleView>(vehicleEndpoints.detail(id), input)
}

export interface ListVehiclesParams {
  page?: number
  pageSize?: number
  q?: string
  vehicleClass?: VehicleClass
  status?: VehicleStatus
}

export function fetchVehicles(params: ListVehiclesParams = {}): Promise<Paginated<VehicleView>> {
  const search = new URLSearchParams()
  if (params.page !== undefined) search.set('page', String(params.page))
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize))
  if (params.q) search.set('q', params.q)
  if (params.vehicleClass) search.set('vehicleClass', params.vehicleClass)
  if (params.status) search.set('status', params.status)
  const query = search.toString()
  return api.get<Paginated<VehicleView>>(
    query.length > 0 ? `${vehicleEndpoints.list}?${query}` : vehicleEndpoints.list,
  )
}
